import {
  WSAlert,
  WSButton,
  WSElement,
  WSFlexBox,
  WSForm,
  WSInputNumber,
  WSList,
  WSPill,
  WSTable,
  WSTableCell,
  WSText,
  useWSSnackbar
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import {
  IMergedRateCard,
  IRateValue
} from "@wingspanhq/payments/dist/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import React, { useState, useCallback } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../../../components/WSErrorMessage/WSErrorMessage";
import { Layout } from "../../../../../shared/components/Layout";
import { useQueryPayee } from "../../../../../query/payee/queries/useQueryPayee";
import { usePayeeEngagementByPayeeIdQuery } from "../../../../../query/payeeEngagements/queries/usePayeeEngagementByPayeeIdQuery";
import { useQueryPayeeEngagementRateCardByPayee } from "../../../../../query/rateCards/queries/useQueryPayeeEngagementRateCardByPayee";
import { useQueryRateCard } from "../../../../../query/rateCards/queries/useQueryRateCard";
import { useCreateRateCardOverride } from "../../../../../query/rateCards/mutations/useCreateRateCardOverride";
import { useUpdateRateCard } from "../../../../../query/rateCards/mutations/useUpdateRateCard";
import { WSQueries } from "../../../../../query/WSQuery";
import {
  buildRateCardCreateRequest,
  buildRateCardUpdateRequest,
  generateOverrideRateCardName,
  getCustomValueKeys,
  hasCustomRateCard,
  hasCustomRateCardId
} from "../../../../RateCards/utils/formatRateCardData";
import { RateCardEditFormData } from "../../../../RateCards/utils/types";
import { useConfirmRateCardEditModal } from "../../../../RateCards/modals/ConfirmRateCardEditModal/useConfirmRateCardEditModal";
import { buildPayeeDetailsEngagementDetailsPath } from "../../../utils";
import styles from "./index.module.scss";

// Component to render custom value cell with access to form values
const CustomValueCell: React.FC<{
  dataKey: string;
  isEditing: boolean;
  isMarkedForDeletion: boolean;
  onToggleEditing: () => void;
  onReset: () => void;
  standardValue: number;
}> = ({
  dataKey,
  isEditing,
  isMarkedForDeletion,
  onToggleEditing,
  onReset,
  standardValue
}) => {
  const { setValue, watch } = useFormContext<RateCardEditFormData>();
  const currentValue = watch(`values.${dataKey}.value`);

  // Show input if user clicked Override OR if value differs from standard
  // BUT hide if key is marked for deletion (user clicked Reset)
  const hasOverride = currentValue !== standardValue;
  const showInput = (isEditing || hasOverride) && !isMarkedForDeletion;

  const handleReset = () => {
    // Reset to standard value
    setValue(`values.${dataKey}.value`, standardValue, {
      shouldValidate: true,
      shouldDirty: true
    });
    setValue(`values.${dataKey}.isEdited`, false, {
      shouldValidate: true,
      shouldDirty: true
    });
    onReset();
  };

  return (
    <WSFlexBox
      gap="S"
      alignItems="flex-start"
      wrap="nowrap"
      className={styles.inputWrapper}
    >
      {showInput ? (
        <WSFlexBox gap="S" alignItems="center" wrap="nowrap">
          <WSForm.Field
            name={`values.${dataKey}.value`}
            component={WSInputNumber}
            componentProps={{
              prefix: "$",
              minFractionDigits: 2,
              maxFractionDigits: 2,
              placeholder: "Enter custom value"
            }}
          />
          <WSButton.Link
            onClick={handleReset}
            type="button"
            className={styles.resetLink}
          >
            Reset
          </WSButton.Link>
        </WSFlexBox>
      ) : (
        <WSButton.Link onClick={onToggleEditing} type="button">
          Override
        </WSButton.Link>
      )}
    </WSFlexBox>
  );
};

// Component to render action buttons with form-aware disabled state
interface ActionButtonsProps {
  standardValues: Record<string, IRateValue>;
  initialValues: Record<string, { value: number; isEdited: boolean }>;
  keysMarkedForDeletion: Set<string>;
  isLoading: boolean;
  onCancel: () => void;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  standardValues,
  initialValues,
  keysMarkedForDeletion,
  isLoading,
  onCancel
}) => {
  const { watch } = useFormContext<RateCardEditFormData>();
  const formValues = watch("values");

  // Check if any value differs from initial values (what was loaded)
  // This detects both new overrides AND resets of existing overrides
  const hasValueChanges = Object.entries(formValues || {}).some(
    ([key, formValue]) => {
      if (!formValue) return false;
      const initialValue = initialValues[key]?.value;
      return (
        formValue.value !== undefined &&
        formValue.value !== null &&
        formValue.value !== initialValue
      );
    }
  );

  // Also check if any keys are marked for deletion
  const hasKeysToDelete = keysMarkedForDeletion.size > 0;
  const hasChanges = hasValueChanges || hasKeysToDelete;

  return (
    <WSFlexBox gap="M" wrap="nowrap">
      <WSElement className={styles.buttonWrapper}>
        <WSButton.Secondary onClick={onCancel} type="button" fullWidth>
          Cancel
        </WSButton.Secondary>
      </WSElement>
      <WSElement className={styles.buttonWrapper}>
        <WSButton.Primary
          type="submit"
          loading={isLoading}
          disabled={!hasChanges && !isLoading}
          fullWidth
        >
          Save changes
        </WSButton.Primary>
      </WSElement>
    </WSFlexBox>
  );
};

type RouteEditRateCardProps = RouteComponentProps<{
  payeeId: string;
  engagementId: string;
  rateCardId: string;
}>;

export const RouteEditRateCard: React.FC<RouteEditRateCardProps> = ({
  match: {
    params: { payeeId, engagementId, rateCardId }
  },
  history
}) => {
  const { openSnackbar } = useWSSnackbar();
  const confirmModal = useConfirmRateCardEditModal();
  const [updateRateCard, updateRateCardMeta] = useUpdateRateCard();
  const [createRateCardOverride, createRateCardOverrideMeta] =
    useCreateRateCardOverride();

  const [editingKeys, setEditingKeys] = useState<Set<string>>(new Set());
  const [keysMarkedForDeletion, setKeysMarkedForDeletion] = useState<
    Set<string>
  >(new Set());

  // Fetch payee data for contractor name
  const queryPayee = useQueryPayee(payeeId);

  // Fetch engagement data
  const queryEngagement = usePayeeEngagementByPayeeIdQuery(
    payeeId,
    engagementId
  );

  // Fetch merged rate card (contains both standard + custom merged)
  const queryMergedRateCard = useQueryPayeeEngagementRateCardByPayee(
    payeeId,
    engagementId,
    { enabled: !!payeeId && !!engagementId }
  );

  const mergedRateCard = queryMergedRateCard.data as
    | IMergedRateCard
    | null
    | undefined;

  // Fetch the standard (engagement) rate card separately to compare values
  const standardRateCardId = mergedRateCard?.engagementRateCardId;
  const hasCustomCard = !!(
    mergedRateCard?.payerPayeeEngagementRateCardId ||
    (mergedRateCard as any)?.payeeEngagementRateCardId
  );
  const queryStandardRateCard = useQueryRateCard(standardRateCardId || "", {
    enabled: !!standardRateCardId && hasCustomCard
  });

  const isLoading =
    updateRateCardMeta.isLoading || createRateCardOverrideMeta.isLoading;
  const error = updateRateCardMeta.error || createRateCardOverrideMeta.error;

  const toggleEditing = useCallback((key: string) => {
    setEditingKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
    // If user clicks Override on a key marked for deletion, remove it from deletion set
    setKeysMarkedForDeletion(prev => {
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });
  }, []);

  const resetEditing = useCallback((key: string) => {
    setEditingKeys(prev => {
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });
    // Mark this key for deletion from custom rate card
    setKeysMarkedForDeletion(prev => {
      const newSet = new Set(prev);
      newSet.add(key);
      return newSet;
    });
  }, []);

  const goBack = () => {
    history.push(buildPayeeDetailsEngagementDetailsPath(payeeId, engagementId));
  };

  return (
    <WSQueries
      queries={{
        queryPayee,
        queryEngagement,
        queryMergedRateCard
      }}
    >
      {({ queryPayeeData, queryEngagementData, queryMergedRateCardData }) => {
        const rateCardData = queryMergedRateCardData as IMergedRateCard;
        const standardRateCard = queryStandardRateCard.data;

        const isCustomCard = hasCustomRateCard(rateCardData, standardRateCard);

        // Use standard rate card values if available, otherwise fall back to merged
        const standardValues = standardRateCard?.values || rateCardData.values;

        // Get current custom keys (keys that are overridden in the existing custom card)
        const currentCustomKeys = getCustomValueKeys(
          rateCardData,
          standardRateCard
        );

        // Get contractor name
        const names = wsName({
          user: {
            email: queryPayeeData.user?.email as string,
            profile: queryPayeeData.user?.profile
          },
          member: queryPayeeData.member
        });
        const contractorName = names.getResolvedName();

        const handleSubmit = async (formData: RateCardEditFormData) => {
          // Build the update request to get values
          const updateRequest = buildRateCardUpdateRequest(
            formData,
            standardValues,
            currentCustomKeys
          );

          // Combine keysToDelete from buildRateCardUpdateRequest with keysMarkedForDeletion
          const allKeysToDelete = [
            ...new Set([
              ...updateRequest.keysToDelete,
              ...Array.from(keysMarkedForDeletion)
            ])
          ];

          // Check if there are any changes (new overrides or keys to delete)
          const hasNewOverrides = Object.keys(updateRequest.values).length > 0;
          const hasKeysToDelete = allKeysToDelete.length > 0;
          const hasChanges = hasNewOverrides || hasKeysToDelete;

          // No changes to save
          if (!hasChanges) {
            openSnackbar({
              message: "No changes to save",
              type: "warning"
            });
            return;
          }

          // Show confirmation modal
          const confirmed = await confirmModal.open({ contractorName });
          if (!confirmed) {
            return;
          }

          try {
            // Use hasCustomRateCardId to check if a custom rate card exists (even if all values were reset)
            // This determines whether to update the existing custom card or create a new one
            if (hasCustomRateCardId(rateCardData)) {
              // Update existing custom rate card
              const customRateCardId =
                rateCardData.payerPayeeEngagementRateCardId ||
                (rateCardData as any).payeeEngagementRateCardId;

              if (!customRateCardId) {
                throw new Error("Custom rate card ID not found");
              }

              await updateRateCard({
                rateCardId: customRateCardId,
                update: { values: updateRequest.values },
                keysToDelete: allKeysToDelete,
                payeeId,
                payeeEngagementId: engagementId
              });
            } else {
              // Create new override rate card from standard card
              const customName = generateOverrideRateCardName(
                rateCardData.name,
                contractorName
              );

              await createRateCardOverride({
                payeeId,
                payeeEngagementId: engagementId,
                rateCardData: buildRateCardCreateRequest(
                  formData,
                  {
                    ...rateCardData,
                    values: standardValues
                  } as IMergedRateCard,
                  rateCardData.userId,
                  customName
                )
              });
            }

            openSnackbar({
              message: "Rate card updated successfully",
              type: "success"
            });

            goBack();
          } catch (err) {
            // Error handled by mutation
          }
        };

        const validationSchema = Yup.object().shape({
          values: Yup.object()
        });

        // Build table data from standard values
        const tableData = Object.entries(standardValues).map(
          ([key, value]) => ({
            data: { ...value, key },
            id: key
          })
        );

        // Build initial values from merged card (what was loaded from server)
        const initialFormValues: Record<
          string,
          { value: number; isEdited: boolean }
        > = {};

        Object.entries(standardValues).forEach(([key, standardValue]) => {
          const mergedValue = rateCardData.values[key];
          const currentValue = mergedValue?.value ?? standardValue.value;
          initialFormValues[key] = {
            value: currentValue,
            isEdited: currentValue !== standardValue.value
          };
        });

        // Build default values using merged card (which has current overrides)
        const buildDefaultValues = (): RateCardEditFormData => {
          return { values: initialFormValues, comment: "" };
        };

        return (
          <Layout title="Edit rate card" onClose={goBack} wide>
            <WSList gap="XL">
              <WSAlert
                theme="info"
                icon="info-circle"
                title="About editing rate cards on assignments"
                my="L"
              >
                Standard rates are inherited from the Engagement. Any edits to
                rates on this assignment will affect the assignee (Contractor)
                only and will appear as a customized rate.
              </WSAlert>

              <WSForm<RateCardEditFormData>
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                defaultValues={buildDefaultValues()}
              >
                <WSList gap="XL">
                  <WSElement>
                    <WSFlexBox alignItems="center" gap="M" mb="M">
                      <WSText.Heading5>Rate card</WSText.Heading5>
                      <WSPill
                        theme={isCustomCard ? "blue" : "neutral"}
                        text={isCustomCard ? "Custom" : "Standard"}
                      />
                    </WSFlexBox>
                    <WSTable
                      tableData={tableData}
                      columns={[
                        {
                          config: {
                            gridTemplateSizeMax: "1fr",
                            header: { text: "Rate" }
                          },
                          renderFunction: ({ data }) => (
                            <WSTableCell text={data.description || data.name} />
                          )
                        },
                        {
                          config: {
                            gridTemplateSizeMax: "1fr",
                            header: { text: "Value" }
                          },
                          renderFunction: ({ data }) => (
                            <WSTableCell
                              text={formatMoney(data.value, Currency.USD)}
                            />
                          )
                        },
                        {
                          config: {
                            gridTemplateSizeMax: "1fr",
                            header: { text: "Custom Value" }
                          },
                          renderFunction: ({ data }) => {
                            const isEditing = editingKeys.has(data.key);
                            const isMarkedForDeletion =
                              keysMarkedForDeletion.has(data.key);
                            return (
                              <CustomValueCell
                                dataKey={data.key}
                                isEditing={isEditing}
                                isMarkedForDeletion={isMarkedForDeletion}
                                onToggleEditing={() => toggleEditing(data.key)}
                                onReset={() => resetEditing(data.key)}
                                standardValue={data.value}
                              />
                            );
                          }
                        }
                      ]}
                    />
                  </WSElement>

                  {error && <WSErrorMessage error={error} contextKey="Other" />}

                  <WSElement mt="S">
                    <ActionButtons
                      standardValues={standardValues}
                      initialValues={initialFormValues}
                      keysMarkedForDeletion={keysMarkedForDeletion}
                      isLoading={isLoading}
                      onCancel={goBack}
                    />
                  </WSElement>
                </WSList>
              </WSForm>
            </WSList>
          </Layout>
        );
      }}
    </WSQueries>
  );
};
